import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { Toast } from 'components';
import { TOAST_TYPES_ENUM, ROUTE_ENUM } from 'enums';
import { useNavigate } from 'react-router';
import { usePWA } from 'hooks/usePWA';
import { mutateWithOffline } from 'utils/mutateWithOffline';

interface BackOrderType {
  orderUuid: string;
  comment: string;
  groupUuid?: string;
}

const useBackOrder = (
  options?: UseMutationOptions<any, Error, BackOrderType>
) => {
  const navigate = useNavigate();
  const { isOffline } = usePWA();
  const queryClient = useQueryClient();

  const removeOrderFromCalendarCache = (orderUuid: string) => {
    const calendarQueryKeys = [
      ['measurerSchedule'],
      ['ordersByEmployee'],
      ['employeeSchedule'],
    ];

    calendarQueryKeys.forEach((baseKey) => {
      const matchingQueries = queryClient.getQueryCache().findAll({
        queryKey: baseKey,
        exact: false,
      });


      matchingQueries.forEach((query) => {
        queryClient.setQueryData(query.queryKey, (oldData: any) => {
          if (!oldData) return oldData;

          if (oldData.orders && Array.isArray(oldData.orders)) {
            const filteredOrders = oldData.orders.filter(
              (order: any) => order.uuid !== orderUuid
            );
            return { ...oldData, orders: filteredOrders };
          }

          if (Array.isArray(oldData)) {
            const filtered = oldData.filter((order: any) => order.uuid !== orderUuid);
            return filtered;
          }

          return oldData;
        });
      });

      queryClient.invalidateQueries({ 
        queryKey: baseKey, 
        exact: false,
        refetchType: 'none'
      });
    });

    try {
      const offlineCache = localStorage.getItem('offlineApiCache');
      if (offlineCache) {
        const cache = JSON.parse(offlineCache);
        let modified = false;

        Object.keys(cache).forEach(url => {
          const cacheEntry = cache[url];
          if (cacheEntry && cacheEntry.data) {
            const data = cacheEntry.data;

            if (data.orders && Array.isArray(data.orders)) {
              const hasOrder = data.orders.some((order: any) => order.uuid === orderUuid);
              
              if (hasOrder) {
                data.orders = data.orders.filter((order: any) => order.uuid !== orderUuid);
                cacheEntry.data = data;
                cache[url] = cacheEntry;
                modified = true;
              }
            }
          }
        });

        if (modified) {
          localStorage.setItem('offlineApiCache', JSON.stringify(cache));
          window.dispatchEvent(new Event('storage'));
        } 
      } 
    } catch (error) {
      console.error('❌ [useBackOrder] error updating localStorage cache:', error);
    }

    try {
      const todayOrdersDetails = localStorage.getItem('offlineTodayOrdersDetails');
      if (todayOrdersDetails) {
        const orders = JSON.parse(todayOrdersDetails);
        if (Array.isArray(orders)) {
          const beforeLength = orders.length;
          const filteredOrders = orders.filter((order: any) => order.uuid !== orderUuid);
          const afterLength = filteredOrders.length;

          if (beforeLength !== afterLength) {
            localStorage.setItem('offlineTodayOrdersDetails', JSON.stringify(filteredOrders));
            
            localStorage.setItem('offlineTodayOrdersCount', String(afterLength));
            localStorage.setItem('offlineTodayOrdersTotalFromCalendar', String(afterLength));
          } 
        }
      }
    } catch (error) {
      console.error('❌ [useBackOrder] error updating offlineTodayOrdersDetails:', error);
    }

    queryClient.invalidateQueries();
    
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'REMOVE_ORDER_FROM_CALENDAR',
        orderUuid: orderUuid,
      });
    }
    
  };

  return useMutation({
    ...options,
    mutationKey: ['backOrder'],
    mutationFn: async ({ orderUuid, comment, groupUuid }: BackOrderType) => {
      const result = await mutateWithOffline(
        'POST',
        '/orders/status-back',
        { orderUuid, comment, groupUuid },
        {
          entityType: 'backOrder',
          forceOffline: isOffline,
          simulateResponse: ({ tempId }) => {
            removeOrderFromCalendarCache(orderUuid);

            return {
              status: 'success',
              message: 'Back order queued (offline)',
              tempId,
            };
          },
        }
      );

      return result;
    },
    onMutate: (variables) => {
      Toast({
        message: isOffline
          ? 'Cofanie statusu... (zostanie wysłane po przywróceniu połączenia)'
          : 'Cofanie statusu pomiaru...',
        type: TOAST_TYPES_ENUM.LOADING,
        id: 'back-measurement',
      });
      if (options && options.onMutate) {
        return options.onMutate(variables);
      }
    },
    onSuccess: (data, variables, context) => {
      if (isOffline) {
        removeOrderFromCalendarCache(variables.orderUuid);
      }

      Toast({
        message: isOffline
          ? 'Status zostanie cofnięty po synchronizacji'
          : 'Status pomiaru został cofnięty pomyślnie',
        type: TOAST_TYPES_ENUM.SUCCESS,
        id: 'back-measurement',
      });

      navigate(ROUTE_ENUM.ORDERS_LIST);

      if (options && options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      Toast({
        message: 'Błąd podczas cofania statusu pomiaru',
        type: TOAST_TYPES_ENUM.ERROR,
        id: 'back-measurement',
      });

      if (options && options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
};

export default useBackOrder;
